import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    TherapistFigureCreate,
    TherapistFigureUpdate,
    TherapistFiguresAPI,
} from "../infrastructure/api/TherapistFigures.api";

const KEY = ["therapist-figures"];

// Therapist (authenticated) — list + manage the catalogue of saved figures.
export const useTherapistFigures = () =>
    useQuery({
        queryKey: KEY,
        queryFn: async () => (await TherapistFiguresAPI.list()).data,
    });

export const useCreateTherapistFigure = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: TherapistFigureCreate) => TherapistFiguresAPI.create(data),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
    });
};

export const useUpdateTherapistFigure = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: TherapistFigureUpdate }) =>
            TherapistFiguresAPI.update(id, data),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
    });
};

export const useDeleteTherapistFigure = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => TherapistFiguresAPI.delete(id),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
    });
};

// Patient (public, token-scoped) — read-only list of the owning therapist's
// figures. Plain query keyed by token; no auth context required.
export const usePublicTherapistFigures = (token: string | undefined) =>
    useQuery({
        queryKey: ["therapist-figures", "public", token],
        queryFn: async () => (await TherapistFiguresAPI.listByToken(token as string)).data,
        enabled: !!token,
    });
